import * as React from 'react';
import { useQueryWithStore, useDataProvider } from 'react-admin';
import { useDispatch } from 'react-redux';
import {
  CircularProgress,
  makeStyles,
  Tabs,
  Tab,
  Theme,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import MenuIcon from '@material-ui/icons/Menu';
import * as hb from '~/types/Handbook';
import GenericRecordTable from '../Handbook/GenericRecordTable';
import KycLevelTable from '../Handbook/KycLeveltable';
import CountryTable from '../Handbook/CountryTable';
import CurrencyTable from '../Handbook/CurrencyTable';
import LocalizationTable from '../Handbook/LocalizationTable';
import OperationSubtypeTable from '../Handbook/OperationSubtypeTable';
import { sanitize } from '../Handbook/utils';
import { handbookUpdate } from '~/ducks/solutions';
import { useReduxState } from '~/hooks';
import Box from '@material-ui/core/Box';
import { useTranslate } from 'react-admin';
import { queryClient } from '~/App';
import { TenantType } from '~/api/utils';
import { tenantQueryKey } from '~/hooks/tenant';

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  const classes = useStyles();
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <div className={classes.formWrapper}>
            <Typography>{children}</Typography>
          </div>
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: any) => {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
};

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    // marginLeft: '-16px',
  },
  tabRoot: {
    maxWidth: '100%',
  },
  wrapper: {
    display: 'inline',
    textAlign: 'left',
  },
  disabledTab: {
    fontSize: 17,
    fontWeight: 600,
    color: theme.palette.text.primary,
    opacity: '1 !important',
  },
  formWrapper: {
    boxSizing: 'border-box',
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(3),
    width: '100%',
  },
  toggleButton: {
    marginBottom: theme.spacing(1),
  },
  collapsedToggle: {
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
}));

export const useUpdateHanbookLoading = () => {
  return useReduxState('handbookUpdating', false);
};

const HandbookField = ({
  initialState,
  solutionId,
}: {
  initialState: hb.Handbook;
  solutionId: string;
}) => {
  const dispatch = useDispatch();
  // main state
  const [state, setState] = React.useState<Partial<hb.Handbook>>(initialState);
  const [, setLoading] = useUpdateHanbookLoading();

  const dataProvider = useDataProvider();

  const updateAndSetState: hb.SetState = (patch) => {
    const newState = sanitize({ ...state, ...patch });
    setLoading(true);
    return dataProvider
      .updateSolutionHandbook('handbook', {
        solutionId,
        handbook: newState,
      })
      .then(() => {
        setState(newState);
        dispatch(handbookUpdate({ solutionId, data: newState }));
        return Promise.resolve();
      })
      .catch((error: any) => {
        console.log('error: ', error);
        return Promise.reject();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // tabs state
  const classes = useStyles();
  const [tabsValue, setTabsValue] = React.useState(0);
  const [tabsCollapsed, setTabsCollapsed] = React.useState(false);

  const handleTabsChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabsValue(newValue);
  };

  const tenant = queryClient.getQueryData<TenantType>(tenantQueryKey);

  const translate = useTranslate();
  const t = (key: string) => translate(`resources.solutions.handbooks.${key}`);

  // render
  return (
    <Grid container>
      {tabsCollapsed ? (
        <Grid item className={classes.collapsedToggle}>
          <Tooltip title={t('showTabs')}>
            <IconButton size='small' onClick={() => setTabsCollapsed(false)}>
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      ) : (
        <Grid item xs={2}>
          <Box display='flex' justifyContent='flex-end'>
            <Tooltip title={t('hideTabs')}>
              <IconButton
                size='small'
                className={classes.toggleButton}
                onClick={() => setTabsCollapsed(true)}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Tabs
            orientation='vertical'
            variant='scrollable'
            value={tabsValue}
            onChange={handleTabsChange}
            className={classes.tabs}
          >
            <Tab
              classes={{
                root: classes.tabRoot,
                wrapper: classes.wrapper,
                disabled: classes.disabledTab,
              }}
              label={t('countries')}
              {...a11yProps(0)}
            />
            <Tab
              classes={{
                root: classes.tabRoot,
                wrapper: classes.wrapper,
                disabled: classes.disabledTab,
              }}
              label={t('currencies')}
              {...a11yProps(1)}
            />
            <Tab
              classes={{
                root: classes.tabRoot,
                wrapper: classes.wrapper,
                disabled: classes.disabledTab,
              }}
              label={t('localizations')}
              {...a11yProps(2)}
            />
            <Tab
              classes={{
                root: classes.tabRoot,
                wrapper: classes.wrapper,
                disabled: classes.disabledTab,
              }}
              label={t('kycLevels')}
              {...a11yProps(3)}
            />
            <Tab
              classes={{
                root: classes.tabRoot,
                wrapper: classes.wrapper,
                disabled: classes.disabledTab,
              }}
              label={t('mcc')}
              {...a11yProps(4)}
            />
            <Tab
              classes={{
                root: classes.tabRoot,
                wrapper: classes.wrapper,
                disabled: classes.disabledTab,
              }}
              label={t('industries')}
              {...a11yProps(5)}
            />
            {solutionId === tenant?.id && (
              <Tab
                classes={{
                  root: classes.tabRoot,
                  wrapper: classes.wrapper,
                  disabled: classes.disabledTab,
                }}
                label={t('branches')}
                {...a11yProps(6)}
              />
            )}
            {solutionId === tenant?.id && (
              <Tab
                classes={{
                  root: classes.tabRoot,
                  wrapper: classes.wrapper,
                  disabled: classes.disabledTab,
                }}
                label={t('operationSubtypes')}
                {...a11yProps(7)}
              />
            )}
          </Tabs>
        </Grid>
      )}
      <Grid item xs={tabsCollapsed ? 12 : 10}>
        <TabPanel value={tabsValue} index={0}>
          <CountryTable handbook={state} setState={updateAndSetState} />
        </TabPanel>
        <TabPanel value={tabsValue} index={1}>
          <CurrencyTable handbook={state} setState={updateAndSetState} />
        </TabPanel>
        <TabPanel value={tabsValue} index={2}>
          <LocalizationTable handbook={state} setState={updateAndSetState} />
        </TabPanel>
        <TabPanel value={tabsValue} index={3}>
          <KycLevelTable handbook={state} setState={updateAndSetState} />
        </TabPanel>
        <TabPanel value={tabsValue} index={4}>
          <GenericRecordTable
            handbook={state}
            setState={updateAndSetState}
            property='merchantCategoryCodes'
          />
        </TabPanel>
        <TabPanel value={tabsValue} index={5}>
          <GenericRecordTable handbook={state} setState={updateAndSetState} property='industries' />
        </TabPanel>
        {solutionId === tenant?.id && (
          <TabPanel value={tabsValue} index={6}>
            <GenericRecordTable handbook={state} setState={updateAndSetState} property='branches' />
          </TabPanel>
        )}
        {solutionId === tenant?.id && (
          <TabPanel value={tabsValue} index={7}>
            <OperationSubtypeTable handbook={state} setState={updateAndSetState} />
          </TabPanel>
        )}
      </Grid>
    </Grid>
  );
};

export interface Props {
  solutionId: string;
}

const HandbookTab = ({ solutionId }: Props) => {
  const { loading, error, data } = useQueryWithStore({
    type: 'getSolutionHandbook',
    resource: 'handbook',
    payload: { solutionId },
  });
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.solutions.handbooks.${key}`);
  if (loading) {
    return <CircularProgress />;
  } else if (error) {
    return <Alert severity='error'>{t('error')}</Alert>;
  } else {
    const initialState: hb.Handbook = data || [];
    return <HandbookField initialState={initialState} solutionId={solutionId} />;
  }
};

export default HandbookTab;
